import React, { useRef } from 'react';
import { LoadingSpinner, ErrorMessage, EmptyState, HorizontalTableShell } from '../ui';
import { useCustomerAveragePurchases, useCustomerAveragePurchasesTotal, SectionProps } from '../../hooks/useCustomerAnalyticsSections';
import { formatNumber } from '../../utils/formatters';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface AveragePurchasesTableProps extends SectionProps {
  branchId: number;
  months?: number;
}

const AveragePurchasesTable: React.FC<AveragePurchasesTableProps> = ({
  branchId,
  months = 12,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  const { data: purchasesData, isLoading, isError, error, refetch } = useCustomerAveragePurchases(branchId, months);
  // Totals row is computed via USP_BI_yearlyOrdersAndCustomersForEachMonth (MonthCount = time frame selector)
  const { data: totalData } = useCustomerAveragePurchasesTotal(branchId, months);
  
  // Component metadata
  const componentId = 'average-purchases-table';
  const componentTitle = 'میانگین خرید مشتریان در ماه';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center h-[400px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری جدول…"
          />
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage 
          message="خطا در دریافت اطلاعات میانگین خرید"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!purchasesData || purchasesData.length === 0) {
    return (
      <div className="card-base card-padding">
        <EmptyState />
      </div>
    );
  }

  // Reverse data for chronological order
  // const reversedData = [...purchasesData].reverse();

  // Totals row sourced from the stored procedure (USP_BI_yearlyOrdersAndCustomersForEachMonth)
  const totalsRow = totalData
    ? {
        totalCustomers: totalData.totalCustomers,
        totalFacture: totalData.totalFacture,
        avgPurchase: totalData.avgPurchase
      }
    : undefined;

  // Enrich data with month-over-month growth of average purchase.
  // Data is returned newest-first, so the previous month is the next index.
  const enrichedData = purchasesData.map((item, index) => {
    const prev = purchasesData[index + 1];
    const growth =
      prev && prev.avgPurchase > 0
        ? ((item.avgPurchase - prev.avgPurchase) / prev.avgPurchase) * 100
        : null;
    return { ...item, growth };
  });

  // Render month-over-month growth with directional color/arrow
  const renderGrowth = (val: number | null) => {
    if (val === null || val === undefined) return <span className="text-gray-400">-</span>;
    const isPositive = val >= 0;
    return (
      <span className={isPositive ? 'text-green-600' : 'text-red-600'}>
        {isPositive ? '▲' : '▼'} {Math.abs(val).toFixed(1)}%
      </span>
    );
  };

  const columns: Array<{ key: string; title: string; render?: (val: any) => React.ReactNode }> = [
    { key: 'month', title: 'ماه' },
    { key: 'totalCustomers', title: 'تعداد کل مشتریان', render: (val) => formatNumber(val) },
    { key: 'totalFacture', title: 'تعداد سفارش مشتریان', render: (val) => formatNumber(val) },
    { key: 'avgPurchase', title: 'میانگین خرید', render: (val) => Number(val).toFixed(2) },
    { key: 'growth', title: 'رشد نسبت به ماه قبل', render: (val) => renderGrowth(val) }
  ];

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="table"
        dashboardSource="customerAnalytics"
        onInfo={() => openGuide(CHART_GUIDES.averagePurchasesTable)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'customerAnalytics',
          componentType: 'table',
          description: 'نمایش میانگین خرید مشتریان در هر ماه',
          props: { branchId, months, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 lg:grid-cols-2 gap-6'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />
      <h3 className="title-card">{componentTitle}</h3>
      <HorizontalTableShell minWidth={Math.max(720, columns.length * 100)}>
          <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
            <thead>
              <tr className="border-b-2 !bg-gray-100 dark:!bg-gray-800" style={{ borderColor: 'var(--border-secondary)' }}>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-3 py-4 text-center text-sm font-bold border-l last:border-l-0 text-text-primary"
                    style={{ borderColor: 'var(--border-primary)' }}
                  >
                    {column.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {enrichedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`transition-all border-b ${rowIndex % 2 === 0 ? '!bg-white dark:!bg-gray-900' : '!bg-gray-50 dark:!bg-gray-800'} hover:!bg-blue-50 dark:hover:!bg-gray-700`}
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  {columns.map((column) => {
                    const value = (row as any)[column.key];
                    const isMonth = column.key === 'month';
                    return (
                      <td
                        key={column.key}
                        className={`px-3 py-4 whitespace-nowrap text-center text-sm border-l last:border-l-0 ${
                          isMonth
                            ? 'font-bold text-text-primary bg-bg-tertiary'
                            : 'text-text-secondary font-medium'
                        }`}
                        style={{ borderColor: 'var(--border-primary)' }}
                      >
                        {column.render ? column.render(value) : value}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Totals row — distinct primary styling (sourced from stored procedure) */}
              {totalsRow && (
                <tr
                  className="border-t-2 bg-primary-100 dark:bg-primary-900/30 font-bold"
                  style={{ borderColor: 'var(--border-secondary)' }}
                >
                  {columns.map((column, index) => {
                    if (index === 0) {
                      return (
                        <td
                          key={column.key}
                          className="px-3 py-4 whitespace-nowrap text-center text-sm font-bold border-l last:border-l-0 text-primary-700 dark:text-primary-300"
                          style={{ borderColor: 'var(--border-primary)' }}
                        >
                          مجموع
                        </td>
                      );
                    }

                    const value = (totalsRow as any)[column.key];
                    const hasValue = value !== undefined && value !== null;
                    return (
                      <td
                        key={column.key}
                        className="px-3 py-4 whitespace-nowrap text-center text-sm font-bold border-l last:border-l-0 text-primary-700 dark:text-primary-300"
                        style={{ borderColor: 'var(--border-primary)' }}
                      >
                        {hasValue && column.render ? column.render(value) : hasValue ? value : '-'}
                      </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
      </HorizontalTableShell>
    </div>
  );
};

export default AveragePurchasesTable;